import { child, push, ref, remove, set, update } from "firebase/database";
import { useMemo } from "react";
import { useObjectVal } from "react-firebase-hooks/database";
import { fbDatabase } from "../firebase";
import { encryptedPlaceholder, encryptField, encryptPollConfig, getPrimarySessionPassphrase, markEncryptedContentDetected, tryDecrypt, tryDecryptPollConfig, useSessionPassphrases } from "../util/encryption";
import { logFirstItemAddedIfFirstTime } from "./analytics";
import { type ItemData, itemTransform } from "./item.types";
import type { PollConfig } from "./poll.types";

export * from "./item.types";

const itemOptions = {
  keyField: 'uid',
  transform: itemTransform,
};

export function refItemsRoot(boardUid: string) { return ref(fbDatabase, `items/${boardUid}`) }
export function refItems(boardUid: string, sessionUid: string) { return child(refItemsRoot(boardUid), sessionUid) }
export function refItem(boardUid: string, sessionUid: string, itemUid: string) { return child(refItems(boardUid, sessionUid), itemUid) }

export function useItems(boardUid: string, sessionUid: string) {
  const passphrases = useSessionPassphrases(sessionUid);
  const [val, loading, error] = useObjectVal<Record<string, Record<string, unknown>>>(refItems(boardUid, sessionUid));
  const items = useMemo(() => {
    if (!val) return undefined;
    return Object.entries(val).map(([key, v]) => {
      const item = itemTransform({ ...(v as Record<string, unknown>), uid: key });
      return decryptItemFields(item, passphrases, sessionUid);
    });
  }, [val, passphrases, sessionUid]);
  return [items, loading, error] as const;
}
export function useItem(boardUid: string, sessionUid: string, itemUid: string) {
  const passphrases = useSessionPassphrases(sessionUid);
  const [val, loading, error] = useObjectVal<ItemData>(refItem(boardUid, sessionUid, itemUid), itemOptions);
  const item = useMemo(() => {
    if (!val) return undefined;
    return decryptItemFields(val, passphrases, sessionUid);
  }, [val, passphrases, sessionUid]);
  return [item, loading, error] as const;
}

export function decryptItemFields(item: ItemData, passphrases: string[], sessionUid: string): ItemData {
  let undecrypted = false;

  const textResult = tryDecrypt(item.text, passphrases);
  const text = textResult ?? encryptedPlaceholder();
  if (textResult === null) undecrypted = true;

  let pollConfig = item.pollConfig;
  if (item.pollConfig) {
    const result = tryDecryptPollConfig(item.pollConfig, passphrases);
    pollConfig = result.config;
    if (result.failed) undecrypted = true;
  }

  if (undecrypted) markEncryptedContentDetected(sessionUid);

  if (text === item.text && pollConfig === item.pollConfig && !undecrypted) {
    return item;
  }
  return {
    ...item,
    text,
    ...(pollConfig !== undefined && { pollConfig }),
    ...(undecrypted && { undecrypted: true }),
  };
}

export const createItem: (
  ownerUid: string,
  boardUid: string,
  sessionUid: string,
  categoryUid: string,
  text: string,
  backgroundImageUrl?: string,
  pollUid?: string,
  pollConfig?: PollConfig,
) => Promise<string | null> = async (ownerUid, boardUid, sessionUid, categoryUid, text, backgroundImageUrl, pollUid, pollConfig) => {
  const newItemRef = await push(refItems(boardUid, sessionUid));
  if (!newItemRef.key) return null;

  const passphrase = getPrimarySessionPassphrase(sessionUid);
  const encText = passphrase ? encryptField(text, passphrase) : text;
  const encPollConfig = passphrase && pollConfig ? encryptPollConfig(pollConfig, passphrase) : pollConfig;

  const epoch = Date.now();
  await set(newItemRef, {
    boardUid: boardUid,
    sessionUid: sessionUid,
    categoryUid: categoryUid,
    ownerUid: ownerUid,
    createdDate: epoch,
    text: encText,
    ...(backgroundImageUrl && { backgroundImageUrl: backgroundImageUrl }),
    ...(pollUid && { pollUid: pollUid }),
    ...(encPollConfig && { pollConfig: encPollConfig }),
  });

  logFirstItemAddedIfFirstTime();
  return newItemRef.key;
}

export const editItem: (
  itemData: Pick<ItemData, 'boardUid' | 'sessionUid' | 'uid'>,
  text: string,
  backgroundImageUrl?: string,
  pollUid?: string | null,
  clearPollData?: boolean,
  pollConfig?: PollConfig,
) => Promise<void> = async (itemData, text, backgroundImageUrl, pollUid, clearPollData, pollConfig) => {
  const passphrase = getPrimarySessionPassphrase(itemData.sessionUid);
  const encText = passphrase ? encryptField(text, passphrase) : text;
  const encPollConfig = passphrase && pollConfig ? encryptPollConfig(pollConfig, passphrase) : pollConfig;

  await update(refItem(itemData.boardUid, itemData.sessionUid, itemData.uid), {
    text: encText,
    ...(backgroundImageUrl && { backgroundImageUrl: backgroundImageUrl }),
    ...(clearPollData
      ? { pollResponses: null, pollUid: null, pollConfig: null }
      : {
          ...(pollUid !== undefined && { pollUid: pollUid || null }),
          ...(encPollConfig && { pollConfig: encPollConfig }),
        }
    ),
  })
}

export async function addItemSupporter(
  boardUid: string,
  sessionUid: string,
  itemUid: string,
  userUid: string,
): Promise<void> {
  await set(child(refItem(boardUid, sessionUid, itemUid), `supporterUids/${userUid}`), true);
}

export async function removeItemSupporter(
  boardUid: string,
  sessionUid: string,
  itemUid: string,
  userUid: string,
): Promise<void> {
  await remove(child(refItem(boardUid, sessionUid, itemUid), `supporterUids/${userUid}`));
}

export async function setItemPinned(
  boardUid: string,
  sessionUid: string,
  itemUid: string,
  pinned: boolean,
): Promise<void> {
  await update(refItem(boardUid, sessionUid, itemUid), { pinned });
}

export async function setItemEditing(
  boardUid: string,
  sessionUid: string,
  itemUid: string,
  userUid: string,
  timestamp: number,
): Promise<void> {
  await set(child(refItem(boardUid, sessionUid, itemUid), "editingBy"), { uid: userUid, timestamp });
}

export async function setItemPollResponse(
  boardUid: string,
  sessionUid: string,
  itemUid: string,
  userUid: string,
  value: string | number,
): Promise<void> {
  // Responses now store option UIDs (not sensitive text), no encryption needed
  await set(child(refItem(boardUid, sessionUid, itemUid), `pollResponses/${userUid}`), value);
}

export async function removeItemPollResponse(
  boardUid: string,
  sessionUid: string,
  itemUid: string,
  userUid: string,
): Promise<void> {
  await remove(child(refItem(boardUid, sessionUid, itemUid), `pollResponses/${userUid}`));
}

export async function clearItemEditing(
  boardUid: string,
  sessionUid: string,
  itemUid: string,
): Promise<void> {
  await remove(child(refItem(boardUid, sessionUid, itemUid), "editingBy"));
}

export async function updateCategoryItemOrder(
  boardUid: string,
  sessionUid: string,
  categoryUid: string,
  order: string[],
): Promise<void> {
  const categoryRef = child(child(ref(fbDatabase, `categories/${boardUid}`), sessionUid), categoryUid);
  await update(categoryRef, {
    order: order,
  });
}

export async function moveItemToCategory(
  boardUid: string,
  sessionUid: string,
  itemUid: string,
  sourceCategoryUid: string,
  sourceOrder: string[],
  targetCategoryUid: string,
  targetOrder: string[],
  insertIndex: number,
): Promise<void> {
  // Update item's categoryUid
  await update(refItem(boardUid, sessionUid, itemUid), { categoryUid: targetCategoryUid });

  // Remove item from source category order
  const newSourceOrder = sourceOrder.filter((uid) => uid !== itemUid);
  const sourceCategoryRef = child(child(ref(fbDatabase, `categories/${boardUid}`), sessionUid), sourceCategoryUid);
  await update(sourceCategoryRef, { order: newSourceOrder });

  // Insert item into target category order at the specified index
  const newTargetOrder = [...targetOrder];
  newTargetOrder.splice(insertIndex, 0, itemUid);
  const targetCategoryRef = child(child(ref(fbDatabase, `categories/${boardUid}`), sessionUid), targetCategoryUid);
  await update(targetCategoryRef, { order: newTargetOrder });
}

export const deleteItem: (
  itemData: Pick<ItemData, 'boardUid' | 'sessionUid' | 'uid'>,
) => Promise<void> = async (itemData) => {
  await remove(refItem(itemData.boardUid, itemData.sessionUid, itemData.uid))
}